import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePageHeaderStore } from "src/store/pageHeaderStore";
import { Button } from "src/components/ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "src/components/ui/Card";
import { ArrowLeft, Trash2, AlertTriangle, User, Mail, Calendar } from "lucide-react";
import { useToast } from "src/components/ui/UseToast";

interface UserDetails {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  status: string;
  createdAt: string;
  lastLogin?: string;
}

const DeleteUser = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('id');
  const { setTitle, setDescription, setActions, reset } = usePageHeaderStore();
  const { toast } = useToast();
  
  const [user, setUser] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState(Boolean(userId));
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  useEffect(() => {
    if (userId) {
      fetchUser(userId);
    } else {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (user) {
      setTitle(`Delete ${user.firstName} ${user.lastName}`);
      setDescription("Permanently remove user account and all associated data");
      setActions(
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/admin/users/view/${userId}`)}
          className="flex items-center gap-1"
        >
          <ArrowLeft size={16} />
          Back to User
        </Button>
      );
    }

    return () => reset();
  }, [user, setTitle, setDescription, setActions, reset, navigate, userId]);

  const fetchUser = async (id: string) => {
    try {
      setLoading(true);
      // TODO: Implement actual API call to fetch user details
      // Simulate API call with mock data
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const mockUser: UserDetails = {
        id: id,
        firstName: "John",
        lastName: "Doe",
        email: "john.doe@example.com",
        role: "student",
        status: "active",
        createdAt: "2024-01-15T10:30:00Z",
        lastLogin: "2024-01-20T14:45:00Z"
      };
      
      setUser(mockUser);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch user details. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!user) return;
    
    const expectedText = `DELETE ${user.firstName} ${user.lastName}`;
    if (confirmText !== expectedText) {
      toast({
        title: "Confirmation Required",
        description: `Please type "${expectedText}" to confirm deletion.`,
        variant: "destructive",
      });
      return;
    }

    setDeleting(true);

    try {
      // TODO: Implement actual delete API call
      console.log("Deleting user:", userId);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      toast({
        title: "Success",
        description: "User deleted successfully!",
        variant: "default",
      });
      
      navigate("/admin/users/list");
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete user. Please try again.",
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  const getRoleBadge = (role: string) => {
    const roleStyles = {
      admin: "bg-purple-100 text-purple-800",
      tutor: "bg-blue-100 text-blue-800",
      student: "bg-gray-100 text-gray-800"
    };
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${roleStyles[role as keyof typeof roleStyles]}`}>
        {role.charAt(0).toUpperCase() + role.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rfpurple-600"></div>
        </div>
      </>
    );
  }

  if (!user) {
    return (
      <>
        <div className="text-center py-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">User Not Found</h2>
          <p className="text-gray-600 mb-4">The user you're trying to delete doesn't exist.</p>
          <Button onClick={() => navigate("/admin/users/list")}>
            Back to Users
          </Button>
        </div>
      </>
    );
  }

  const expectedConfirmText = `DELETE ${user.firstName} ${user.lastName}`;

  return (
    <>
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Warning Card */}
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-800">
              <AlertTriangle size={20} />
              Danger Zone
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <p className="text-red-700 font-medium">
                You are about to permanently delete this user account.
              </p>
              <div className="bg-red-100 p-3 rounded-md">
                <h4 className="font-medium text-red-800 mb-2">This action will:</h4>
                <ul className="text-sm text-red-700 space-y-1">
                  <li>• Permanently delete the user account</li>
                  <li>• Remove all user data and preferences</li>
                  <li>• Cancel any active sessions or bookings</li>
                  <li>• Delete all user-generated content</li>
                  <li>• This action cannot be undone</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* User Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User size={20} />
              User to be Deleted
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name
                  </label>
                  <p className="text-lg font-semibold text-gray-900">
                    {user.firstName} {user.lastName}
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role
                  </label>
                  <div>{getRoleBadge(user.role)}</div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <div className="flex items-center gap-2">
                  <Mail size={16} className="text-gray-400" />
                  <p className="text-gray-900">{user.email}</p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Member Since
                </label>
                <div className="flex items-center gap-2">
                  <Calendar size={16} className="text-gray-400" />
                  <p className="text-gray-900">
                    {new Date(user.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Confirmation */}
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-800">
              <Trash2 size={20} />
              Confirm Deletion
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-gray-700">
                To confirm deletion, please type{" "}
                <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">
                  {expectedConfirmText}
                </code>{" "}
                in the field below:
              </p>
              
              <div>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={expectedConfirmText}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => navigate(`/admin/users/view/${userId}`)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDeleteUser}
                  disabled={deleting || confirmText !== expectedConfirmText}
                  className="bg-red-600 hover:bg-red-700 text-white flex items-center gap-2"
                >
                  {deleting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} />
                      Delete User Permanently
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default DeleteUser;
